import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CONTRACTS_PAGE, ERROR_CONSTS } from 'src/constants';
import history from 'src/history';
import { useContractBuilderPage } from 'src/hooks/useContractBuilderPage';
import { FormPage } from 'src/components/Page/FormPage';
import { BreadCrumbBase } from 'src/components/UI/BreadCrumbBase';
import {
  ContractBuilderContext,
  ContractFile,
} from 'src/context/contractBuilderContext';
import {
  ResetESignToInitialValues,
  loadContractPages,
} from 'src/store/signaturePage/actions';
import { getFileNameFromFileKey } from '../Files/helpers';
import {
  getContractInputValueUsingComponentType,
  hasValidClientComponents,
} from 'src/utils/SignatureUtils';
import { RootState } from 'src/store';
import { alertSnackbar } from 'src/store/ui/actions';
import {
  AddContractTemplateInput,
  useAddContractTemplateMutation,
} from 'src/services/api';
import { ContractPageTabs } from './ContractsTablePage';
import { ContractField } from 'src/entities/Contract';

export interface ContractTemplateForm {
  title: string;
  fileKey: string;
}

/**
 * This component represents the creation page for contract templates.
 *
 * @returns {JSX.Element} The JSX element rendering the contract template creation page.
 */
export const ContractsBuilderPage = () => {
  const dispatch = useDispatch();
  const pageComponents = useSelector(
    (state: RootState) => state.signaturePage.pageComponents,
  );

  const [addContractTemplate] = useAddContractTemplateMutation();

  // State to hold information about the uploaded PDF file:
  // - 'fileKey' - The key to access the file from S3.
  // - 'pageFileKeys' - An array of keys for page images saved in S3.
  const [contractPdfInfo, setContractPdfInfo] = useState<ContractFile>({
    fileKey: '',
    pageFileKeys: [],
  });

  // State to hold a reference to the placeholder element.
  // This element is used as an anchor for positioning signature popups.
  const [
    placeholderElement,
    setPlaceholderElement,
  ] = useState<HTMLButtonElement | null>(null);

  const initialFormValues: ContractTemplateForm = {
    title: '',
    fileKey: '',
  };

  const navigateToContractsPage = () =>
    history.push(`${CONTRACTS_PAGE.path}?view=${ContractPageTabs.Templates}`);

  /**
   * Handles the save action for creating a contract template.
   * Redirects to the contracts table page upon successful template creation.
   * @param {ContractTemplateForm} values - The form values containing template information.
   */
  const handleSave = async (values: ContractTemplateForm) => {
    // Check if there is at least one valid client signature input to create a template
    if (!hasValidClientComponents(pageComponents)) {
      dispatch(
        alertSnackbar({
          errorMessage: ERROR_CONSTS.AddComponentForClient,
        }),
      );
      return;
    }

    const fields: ContractField[] = pageComponents.map(p => ({
      id: p.key,
      label: p.label,
      // TODO: remove these nullish coallesing operators when we fix the types
      page: p.page ?? 0,
      type: p.fieldType ?? '',
      inputType: p.inputType,
      isOptional: Boolean(p.isOptional),
      value: getContractInputValueUsingComponentType(p),
      autoFillField: p.autoFillField,
      bounds: {
        x: p.xPosition,
        y: p.yPosition,
        height: p.height,
        width: p.width,
      },
    }));

    // Prepare the request body for creating the contract template
    const requestBody: AddContractTemplateInput = {
      name: values.title,
      fileKey: contractPdfInfo.fileKey,
      fields,
    };

    // Send a request to add the contract template
    await addContractTemplate(requestBody);

    // Navigate to the contracts table page after successful creation
    navigateToContractsPage();
  };

  const onCancel = () => {
    navigateToContractsPage();
  };

  /* When the user is redirected to other pages (for example, when the contract creation flow is canceled or completed),
   * we want the signaturePage reducer to reset to its initial state.
   * This ensures that when they return to create a new * contract, they begin with a clean slate.
   */
  useEffect(() => {
    if (contractPdfInfo?.pageFileKeys?.length) {
      dispatch(loadContractPages(contractPdfInfo));
    }

    return () => {
      dispatch(ResetESignToInitialValues());
    };
  }, [contractPdfInfo]);

  // Define the states and methods to be passed down using the `ContractBuilderContext`.
  const contextValue: ContractBuilderContext = useMemo(
    () => ({
      placeholderElement,
      contractPdfInfo,
      setPlaceholderElement,
      setContractPdfInfo,
    }),
    [placeholderElement, contractPdfInfo],
  );

  // breadcrumb items for the builder page.
  // By default, it includes a 'New contract' item, and if a contract PDF file key exists,
  // it appends a file-specific item with the file name.
  const breadcrumbItems = useMemo(
    () => [
      {
        label: 'New contract',
      },
      ...(contractPdfInfo.fileKey
        ? [
            {
              label: getFileNameFromFileKey(contractPdfInfo.fileKey),
            },
          ]
        : []),
    ],
    [contractPdfInfo.fileKey],
  );

  return (
    <ContractBuilderContext.Provider value={contextValue}>
      <FormPage
        useFormHook={useContractBuilderPage}
        headerVariant="bottomBorder"
        BreadcrumbsComponent={<BreadCrumbBase items={breadcrumbItems} />}
        cancelLabel="Cancel"
        actionLabel="Create template"
        handleCancel={onCancel}
        shouldShowPageAction
        handleSave={handleSave}
        initialFormValue={initialFormValues}
      />
    </ContractBuilderContext.Provider>
  );
};
